import React, { useEffect, useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { PasswordPolicyForm } from './PasswordPolicyForm';
import { ApiResponse } from '../../types/api';
import { SecuritySettings, UpdateSecuritySettingsDto } from '../../types/audit';
import { Save } from 'lucide-react';

interface SecuritySettingsFormProps {
  settings: SecuritySettings;
  isSaving?: boolean;
  onSave: (dto: UpdateSecuritySettingsDto) => Promise<ApiResponse<SecuritySettings>>;
}

export const SecuritySettingsForm: React.FC<SecuritySettingsFormProps> = ({ settings, isSaving, onSave }) => {
  const [draft, setDraft] = useState<SecuritySettings>(settings);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => { setDraft(settings); }, [settings]);
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 3000);
    return () => clearTimeout(t);
  }, [message]);

  const patch = (p: UpdateSecuritySettingsDto) => setDraft(prev => ({ ...prev, ...p }));

  const requiredCharacterTypes = [
    draft.password_require_uppercase && 'uppercase letters',
    draft.password_require_numbers && 'numbers',
    draft.password_require_special && 'special characters',
  ].filter(Boolean) as string[];

  const handleSave = async () => {
    const res = await onSave({
      password_min_length: draft.password_min_length,
      password_require_uppercase: draft.password_require_uppercase,
      password_require_numbers: draft.password_require_numbers,
      password_require_special: draft.password_require_special,
      session_timeout_minutes: draft.session_timeout_minutes,
      max_login_attempts: draft.max_login_attempts,
      lockout_duration_minutes: draft.lockout_duration_minutes,
      access_token_expiry_minutes: draft.access_token_expiry_minutes,
      refresh_token_expiry_days: draft.refresh_token_expiry_days,
      enforce_password_history: draft.enforce_password_history,
      require_2fa: draft.require_2fa,
    });
    setMessage(res.success
      ? { type: 'success', text: 'Security settings saved successfully.' }
      : { type: 'error', text: res.message || 'Failed to save security settings.' });
  };

  return (
    <div className="space-y-5">
      {message && (
        <div className={`text-xs px-3 py-2 rounded-lg border ${
          message.type === 'success'
            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
            : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card title="Password Policy" subtitle="Rules enforced when users set or change passwords">
          <PasswordPolicyForm settings={draft} onChange={patch} />
        </Card>

        <Card title="Session Settings" subtitle="Login session and lockout behaviour">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input
              type="number" min={5} max={480}
              label="Session Timeout (min)"
              value={draft.session_timeout_minutes}
              onChange={e => patch({ session_timeout_minutes: Number(e.target.value) })}
            />
            <Input
              type="number" min={1} max={20}
              label="Max Login Attempts"
              value={draft.max_login_attempts}
              onChange={e => patch({ max_login_attempts: Number(e.target.value) })}
            />
            <Input
              type="number" min={1} max={1440}
              label="Lockout Duration (min)"
              value={draft.lockout_duration_minutes}
              onChange={e => patch({ lockout_duration_minutes: Number(e.target.value) })}
            />
          </div>
        </Card>

        <Card title="Token Settings" subtitle="JWT access and refresh token expiry">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              type="number" min={5} max={1440}
              label="Access Token Expiry (min)"
              value={draft.access_token_expiry_minutes}
              onChange={e => patch({ access_token_expiry_minutes: Number(e.target.value) })}
            />
            <Input
              type="number" min={1} max={90}
              label="Refresh Token Expiry (days)"
              value={draft.refresh_token_expiry_days}
              onChange={e => patch({ refresh_token_expiry_days: Number(e.target.value) })}
            />
          </div>
        </Card>

        <Card title="Compliance Summary" subtitle="Derived effective policy">
          <ul className="text-xs text-slate-400 space-y-2">
            <li>Passwords must be at least <span className="text-slate-200 font-medium">{draft.password_min_length}</span> characters.</li>
            <li>{requiredCharacterTypes.length > 0 ? `${requiredCharacterTypes.join(', ')} required.` : 'No character-type requirements.'}</li>
            <li>Sessions auto-expire after <span className="text-slate-200 font-medium">{draft.session_timeout_minutes}</span> minutes of inactivity.</li>
            <li>Accounts lock after <span className="text-slate-200 font-medium">{draft.max_login_attempts}</span> failed attempts for <span className="text-slate-200 font-medium">{draft.lockout_duration_minutes}</span> minutes.</li>
            <li>Two-factor authentication is {draft.require_2fa ? 'enabled' : 'disabled'}.</li>
          </ul>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button variant="primary" isLoading={isSaving} onClick={handleSave} icon={<Save className="w-4 h-4" />}>
          Save Settings
        </Button>
      </div>
    </div>
  );
};
